import { useState } from "react";
import { Radio, RadioGroup } from "react-aria-components";

import { MerryButton } from "@/shared/ui/MerryButton";
import { HomeGlass } from "@/features/merchant-home/ui/MerchantHomePrimitives";
import "@/features/merchant-home/ui/merchant-home.css";

export interface MerchantHomeBindingCardProps {
  operationId?: string;
  platform?: string;
  domain?: string;
  state: "needs_input" | "ready" | "waiting" | "confirming" | "succeeded" | "failed" | "canceled" | "expired";
  message?: string;
  syncState?: "syncing" | "healthy" | "sync_error" | "reauthorization_required";
  shops?: readonly { id: string; name: string }[];
  selectedShopId?: string;
  isSelectingShop?: boolean;
  onAuthorize?: () => void;
  onRetry?: () => void;
  onSelectShop?: (shopId: string) => void;
}

const stateTitles = { needs_input: "补充店铺信息", ready: "前往平台完成授权", waiting: "等待平台授权", confirming: "正在确认授权结果", succeeded: "店铺已绑定", failed: "授权未完成", canceled: "授权已取消", expired: "本次授权已超时" };
const stateDescriptions = { needs_input: "请先在对话中确认平台与店铺域名。", ready: "登录官方平台并选择需要绑定的店铺。", waiting: "请在已打开的官方授权窗口完成操作。", confirming: "正在读取平台确认结果，请稍候。", succeeded: "授权完成后，Merry 将继续当前对话。", failed: "你可以重新授权，当前对话与店铺信息会保留。", canceled: "需要继续时，可以重新打开授权窗口。", expired: "请重新发起授权以继续。" };
const syncDescriptions = { syncing: "店铺资料同步中；经营能力将在资料和权限就绪后开放。", healthy: "店铺资料已同步，后续操作仍会核对所需平台权限。", sync_error: "店铺已绑定，资料同步遇到问题，请稍后重试同步。", reauthorization_required: "店铺需要重新授权后才能继续经营操作。" };

export function MerchantHomeBindingCard(props: MerchantHomeBindingCardProps) {
  return <BindingCardContent key={props.operationId ?? "preparing"} {...props} />;
}

function BindingCardContent({ platform, domain, state, message, syncState, shops = [], selectedShopId, isSelectingShop, onAuthorize, onRetry, onSelectShop }: MerchantHomeBindingCardProps) {
  const [choice, setChoice] = useState(selectedShopId ?? "");
  const canRetry = state === "failed" || state === "canceled" || state === "expired";
  return <HomeGlass className="merchant-home-binding" data-testid="merchant-home-binding">
    <p className="merchant-home__eyebrow">绑定店铺</p><h3>{stateTitles[state]}</h3>
    {(platform || domain) && <dl className="merchant-home-binding__identity">{platform && <div><dt>平台</dt><dd>{platform}</dd></div>}{domain && <div><dt>店铺</dt><dd>{domain}</dd></div>}</dl>}
    <p role={state === "failed" ? "alert" : "status"} aria-live="polite">{message ?? stateDescriptions[state]}</p>
    {state === "succeeded" && syncState && <p className="merchant-home-binding__sync">{syncDescriptions[syncState]}</p>}
    {state === "succeeded" && shops.length > 1 && <div className="merchant-home-binding__shops"><RadioGroup aria-label="选择本次对话使用的店铺" value={selectedShopId ?? choice} onChange={setChoice} isDisabled={isSelectingShop || Boolean(selectedShopId)}><p>选择本次对话使用的店铺</p>{shops.map(shop => <Radio className="merchant-home-binding__shop" value={shop.id} key={shop.id}><span className="merchant-home__radio-dot" />{shop.name}</Radio>)}</RadioGroup><MerryButton hierarchy="level3" isDisabled={!choice || Boolean(selectedShopId) || isSelectingShop} onPress={() => onSelectShop?.(choice)}>{selectedShopId ? "已确认店铺" : isSelectingShop ? "正在确认…" : "确认使用这家店铺"}</MerryButton></div>}
    {state === "ready" && <div className="merchant-home__actions"><MerryButton hierarchy="level3" isDisabled={!platform || !onAuthorize} onPress={onAuthorize}>授权绑定</MerryButton></div>}
    {canRetry && <div className="merchant-home__actions"><MerryButton hierarchy="level3" isDisabled={!onRetry} onPress={onRetry}>重新授权</MerryButton></div>}
  </HomeGlass>;
}
